import React, { useState, useRef } from "react"
import Section from "../../section.component"
import styled, { css } from "styled-components"
import { Swiper, SwiperSlide } from "swiper/react"
import SwiperCore, { Navigation, Pagination } from "swiper"
import "swiper/swiper-bundle.min.css"
import { ReviewsEmbedGridConfig } from "./reviews-embed-grid.interface"

// Initialize Swiper modules
SwiperCore.use([Navigation, Pagination])

const ReviewsEmbedGrid = ({ config }: { config: ReviewsEmbedGridConfig }) => {
  const [activeIndex, setActiveIndex] = useState(1)
  const [playingVideo, setPlayingVideo] = useState<string | null>(null)
  const swiperRef = useRef<SwiperCore | null>(null)

  const handlePlayVideo = (embedId: string, index: number) => {
    if (index === activeIndex) {
      setPlayingVideo(embedId)
    }
  }

  return (
    <ReviewsEmbedGridSection $paddingTop={config.reviewsGrid.paddingTop}>
      <TitleContainer>
        <Title>{config.reviewsGrid.title}</Title>
        <Subtitle>{config.reviewsGrid.subTitle}</Subtitle>
      </TitleContainer>

      <SliderContainer>
        <StyledSwiper
          slidesPerView={1}
          spaceBetween={24}
          centeredSlides
          initialSlide={1}
          threshold={15}
          pagination={{
            el: ".swiper-pagination",
            clickable: true,
            bulletClass: "swiper-pagination-bullet",
            bulletActiveClass: "swiper-pagination-bullet-active",
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
            // Ensure proper initialization
            setTimeout(() => {
              if (swiper) {
                swiper.update()
                swiper.slideTo(1, 0)
              }
            }, 100)
          }}
          breakpoints={{
            768: {
              slidesPerView: 1.8,
              spaceBetween: 0,
              centeredSlides: true,
            },
          }}
        >
          {config.reviewsGrid.embedIds.map((embedId, index) => (
            <SwiperSlide
              key={embedId}
              onClick={() => swiperRef.current?.slideTo(index)}
            >
              <SlideItem $isActive={index === activeIndex}>
                {playingVideo === embedId ? (
                  <iframe
                    height="350"
                    src={`https://www.youtube.com/embed/${embedId}?autoplay=1`}
                    title="YouTube video player"
                    allowFullScreen
                  />
                ) : (
                  <ThumbnailContainer
                    onClick={() => handlePlayVideo(embedId, index)}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${embedId}/hqdefault.jpg`}
                      alt="Video thumbnail"
                    />
                    <PlayButton />
                  </ThumbnailContainer>
                )}
              </SlideItem>
            </SwiperSlide>
          ))}
        </StyledSwiper>
        <PaginationContainer>
          <div className="swiper-pagination"></div>
        </PaginationContainer>
      </SliderContainer>
    </ReviewsEmbedGridSection>
  )
}

export default ReviewsEmbedGrid

const ReviewsEmbedGridSection = styled(Section)<{ $paddingTop?: string }>`
  padding: 64px 0;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    padding: 64px 20px;
    ${({ $paddingTop }) =>
      $paddingTop &&
      css`
        padding-top: ${$paddingTop};
      `}
  }
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 24px;
  max-width: 900px;
  margin: 0 auto;
  margin-bottom: 80px;

  @media (max-width: ${({ theme }) =>
      theme.breakpoint.tabletHorizontal - 1}px) {
    margin-bottom: 48px;
  }
`

const Title = styled.h2`
  text-align: center;

  /* Heading/H1 */
  font-family: Facto;
  font-size: 23px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  margin: 0;

  @media (max-width: ${({ theme }) =>
      theme.breakpoint.tabletHorizontal - 1}px) {
    font-family: Facto;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%;
  }
`

const Subtitle = styled.h3`
  text-align: center;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  margin: 0;

  @media (max-width: ${({ theme }) =>
      theme.breakpoint.tabletHorizontal - 1}px) {
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
  }
`

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`

const StyledSwiper = styled(Swiper)`
  width: 100%;

  @media (max-width: ${({ theme }) =>
      theme.breakpoint.tabletHorizontal - 1}px) {
    padding: 0 0px;
    width: calc(100vw - 40px);
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    overflow: visible !important;
  }

  .swiper-wrapper {
    align-items: center;
  }
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;

  .swiper-pagination {
    position: static !important;
    display: flex;
    justify-content: center;
    gap: 8px;

    .swiper-pagination-bullet {
      width: 8px;
      height: 8px;
      background-color: ${({ theme }) => theme.color.border};
      opacity: 1;
      margin: 0;
      transition: all 0.3s ease;
      cursor: pointer;
      border-radius: 50%;

      &.swiper-pagination-bullet-active {
        background-color: black;
      }

      &:hover {
        background-color: black;
      }
    }
  }
`

const ThumbnailContainer = styled.div`
  position: relative;
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
  }
`

const PlayButton = styled.div`
  position: absolute;
  width: 72px;
  height: 50px;
  background-color: #ff0000;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: #e62117;
    transform: scale(1.1);
  }

  &::after {
    content: "";
    display: block;
    width: 0;
    height: 0;
    border-left: 20px solid white;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    margin-left: 5px;
  }
`

const SlideItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.color.backgroundSuperLight};
  border-radius: 12px;
  overflow: hidden;
  height: 370px;
  width: 640px;
  position: relative;
  transition: all 0.3s ease;

  /* Desktop: Scale and filter non-active slides */
  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    transform: ${({ $isActive }) => ($isActive ? "scale(1)" : "scale(0.85)")};
    filter: ${({ $isActive }) =>
      $isActive ? "none" : "grayscale(100%) brightness(0.7)"};
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0.6)};
  }

  /* Mobile: All slides same size and color */
  @media (max-width: ${({ theme }) =>
      theme.breakpoint.tabletHorizontal - 1}px) {
    height: 220px;
    width: 100%;
    transform: none;
    filter: none;
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border-radius: 12px;
  }
`
